import { useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { uploadProposal } from '../api/shares'
import { colors, radii, shadow } from '../theme'

/**
 * Publish a PDF that wasn't built by this portal.
 *
 * Not every proposal comes out of the generator — some are built in Canva or
 * Docs. Those still deserve a tracked proposals.parkify.com link, so an upload
 * produces the same record shape and shares through the identical path.
 */
export default function UploadProposalModal({
  onClose,
  onUploaded,
}: {
  onClose: () => void
  onUploaded: (id: string) => void
}) {
  const { authHeaders, apiBase } = useAuth()
  const [file, setFile] = useState<File | null>(null)
  const [company, setCompany] = useState('')
  const [prospect, setProspect] = useState('')
  const [city, setCity] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    if (!file || !company.trim()) return
    setBusy(true)
    setError(null)
    try {
      const rec = await uploadProposal(apiBase, authHeaders(), {
        file,
        company: company.trim(),
        prospect: prospect.trim(),
        city: city.trim(),
      })
      onUploaded(rec.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Upload a proposal</h2>
        <p style={{ marginTop: 0, fontSize: 13, color: colors.textMuted }}>
          PDF only, up to 25 MB. It gets the same shareable link and view
          analytics as a generated proposal.
        </p>

        {error && (
          <div
            style={{
              background: colors.warningBg,
              border: `1px solid ${colors.warningBorder}`,
              borderRadius: radii.md,
              padding: '10px 12px',
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          style={{ marginBottom: 12, fontSize: 13 }}
        />
        <input
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Company (required — becomes the link's name)"
          style={input}
        />
        <input
          value={prospect}
          onChange={(e) => setProspect(e.target.value)}
          placeholder="Prospect name"
          style={input}
        />
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="City"
          style={input}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={ghost}>
            Cancel
          </button>
          <button
            disabled={busy || !file || !company.trim()}
            onClick={() => void submit()}
            style={{
              ...primary,
              background: busy || !file || !company.trim() ? colors.gray : colors.purple,
              cursor: busy || !file || !company.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  )
}

const backdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(35,31,32,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  zIndex: 50,
}

const modal: React.CSSProperties = {
  background: colors.panel,
  borderRadius: radii.lg,
  boxShadow: shadow.modal,
  padding: 24,
  width: '100%',
  maxWidth: 460,
}

const input: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: `1px solid ${colors.divider}`,
  borderRadius: radii.md,
  fontSize: 13,
  marginBottom: 10,
}

const primary: React.CSSProperties = {
  color: colors.white,
  border: 'none',
  borderRadius: radii.md,
  padding: '10px 18px',
  fontSize: 13,
  fontWeight: 600,
}

const ghost: React.CSSProperties = {
  background: 'transparent',
  color: colors.black,
  border: `1px solid ${colors.divider}`,
  borderRadius: radii.md,
  padding: '10px 16px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
}
